import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Navigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { getAuthHeaders } from '../../lib/api';
import { isMasqueradeAdminEmail, startMasquerade } from '../../lib/masquerade';

const AdminMasquerade = () => {
  const { user, loading } = useAuth();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [actingId, setActingId] = useState(null);
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setSearchError('');
      setSearching(false);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setSearching(true);
      setSearchError('');
      try {
        const headers = await getAuthHeaders();
        const res = await fetch(`/api/admin/masquerade-search?q=${encodeURIComponent(trimmed)}`, { headers });
        const payload = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) {
          setResults([]);
          setSearchError(payload.error || 'Search failed');
          return;
        }
        setResults(payload.users || []);
      } catch (err) {
        if (!cancelled) {
          setResults([]);
          setSearchError(err?.message || 'Search failed');
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading…</div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (!isMasqueradeAdminEmail(user.email)) return <Navigate to="/" replace />;

  const handleMasquerade = async (targetUserId) => {
    setActingId(targetUserId);
    setActionError('');
    try {
      await startMasquerade(targetUserId);
    } catch (err) {
      setActionError(err?.message || 'Could not masquerade as that user');
      setActingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Masquerade | Nurture Buddy.ai</title>
      </Helmet>
      <Header />
      <main className="pt-16 pb-20 md:pb-0">
        <div className="max-w-2xl mx-auto px-6 py-10">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="Eye" size={22} />
            <h1 className="font-heading text-2xl font-semibold text-foreground">Masquerade</h1>
          </div>
          <p className="text-sm text-muted-foreground mb-6">
            Search for a user by name or email and open the app as them. Your own session stays in this tab so you can exit.
          </p>

          <Input
            label="Find a user"
            placeholder="Name or email"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />

          {searching && (
            <p className="mt-4 text-sm text-muted-foreground">Searching…</p>
          )}
          {searchError && (
            <p className="mt-4 text-sm text-error">{searchError}</p>
          )}
          {actionError && (
            <p className="mt-4 text-sm text-error">{actionError}</p>
          )}

          {!searching && query.trim().length >= 2 && results.length === 0 && !searchError && (
            <p className="mt-4 text-sm text-muted-foreground">No matching users.</p>
          )}

          {results.length > 0 && (
            <ul className="mt-4 divide-y divide-border rounded-lg border border-border bg-card">
              {results.map((person) => (
                <li key={person.id} className="flex items-center justify-between gap-4 px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{person.full_name || 'Unnamed user'}</p>
                    <p className="text-xs text-muted-foreground truncate">{person.email}</p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleMasquerade(person.id)}
                    disabled={actingId !== null}
                  >
                    {actingId === person.id ? 'Opening…' : 'Masquerade'}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminMasquerade;
